import React, { useCallback, useEffect, useState } from "react";
import { LoadingSpinner } from "@xapp/components";

export interface AsyncComponentProps {}

export interface AsyncComponentState<P, S> {
    Component: React.Component<P, S>;
}

type LoadState<P, S> = { Component: React.Component<P, S> } | { error: unknown };

/**
 * A wrapper to return a component asynchronously.
 *
 * Shows a spinner while the component loads. If loading fails (e.g. a chunk could not be
 * downloaded after a deploy), it shows an error with a reload button rather than a blank page.
 *
 * Help from https://github.com/mozilla-neutrino/react-router-starter/blob/master/src/components/asyncComponent.js
 * and https://github.com/mesteche/react-redux-starter-kit/blob/react-router-v4/src/containers/AsyncComponent.js
 * @export
 * @param {() => Promise<React.Component<P, S>>} getComponent
 * @returns
 */
export function asyncComponent<P, S>(getComponent: () => Promise<React.Component<P, S>>): React.FC<P> {
    return useCallback(
        (props: P) => {
            const [loaded, setLoaded] = useState<LoadState<P, S>>();

            useEffect(() => {
                let disposed = false;
                getComponent().then(
                    (comp) => {
                        if (!disposed) {
                            setLoaded({
                                Component: comp,
                            });
                        }
                    },
                    (error) => {
                        console.error(error);
                        if (!disposed) {
                            setLoaded({ error });
                        }
                    }
                );

                return () => {
                    disposed = true;
                };
            }, []);

            if (!loaded) {
                return <LoadingSpinner loadingText="Loading" />;
            }

            if ("error" in loaded) {
                return (
                    <section data-async-component-error role="alert" style={{ padding: 24, textAlign: "center" }}>
                        <p>This page failed to load. A new version of Studio may have been released.</p>
                        <button data-async-component-reload type="button" onClick={() => window.location.reload()}>
                            Reload the page
                        </button>
                    </section>
                );
            }

            const Component = loaded.Component as any;
            return <Component {...props} />;
        },
        [getComponent]
    );
}
